# components/gitlab/ProjectCard.vue
<template>
  <v-hover v-slot="{ hover }">
    <v-card
        :elevation="hover ? 8 : 2"
        :class="{ 'on-hover': hover }"
        class="mx-auto project-card"
    >
      <v-card-title class="project-header" :style="{ backgroundColor: getProjectColor(project) }" @click="showDetails">
        <v-avatar size="40" class="mr-3" v-if="project.avatarUrl">
          <v-img :src="project.avatarUrl"></v-img>
        </v-avatar>
        <v-icon v-else size="40" class="mr-3">mdi-gitlab</v-icon>
        <div class="d-flex flex-column">
          <div class="text-subtitle-1 font-weight-bold text-truncate">
            <a class="text-decoration-none">
              {{ project.name }}
            </a>
          </div>
          <div class="caption">
            {{ project.pathWithNamespace }}
            <v-chip x-small :color="getVisibilityColor(project.visibility)" text-color="white" class="ml-1">
              {{ project.visibility }}
            </v-chip>
          </div>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text @click="showDetails">
        <div class="description-text mb-3">
          {{ project.description || 'No description' }}
        </div>

        <v-list dense class="transparent pa-0">
          <v-list-item class="px-0 py-1">
            <v-list-item-icon class="mr-2 my-2">
              <v-icon small>mdi-source-branch</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-subtitle>
                Default Branch: <strong>{{ project.defaultBranch }}</strong>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-list-item class="px-0 py-1">
            <v-list-item-icon class="mr-2 my-2">
              <v-icon small>mdi-clock-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-subtitle>
                Last Activity: <strong>{{ formatDate(project.lastActivityAt) }}</strong>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-list-item class="px-0 py-1">
            <v-list-item-icon class="mr-2 my-2">
              <v-icon small>mdi-calendar</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-subtitle>
                Created: <strong>{{ formatDate(project.createdAt) }}</strong>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-list-item class="px-0 py-1" v-if="project.namespace">
            <v-list-item-icon class="mr-2 my-2">
              <v-icon small>mdi-folder-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-subtitle>
                Group:
                <a :href="project.namespace.webUrl" target="_blank" class="text-decoration-none">
                  <strong>{{ project.namespace.name }}</strong>
                </a>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <div class="d-flex flex-wrap gap-1 mt-2">
          <v-chip x-small :color="project.issuesEnabled ? 'success' : 'grey'" label outlined>
            Issues {{ project.issuesEnabled ? 'enabled' : 'disabled' }}
          </v-chip>
          <v-chip x-small :color="project.mergeRequestsEnabled ? 'success' : 'grey'" label outlined>
            MR {{ project.mergeRequestsEnabled ? 'enabled' : 'disabled' }}
          </v-chip>
          <v-chip x-small :color="project.jobsEnabled ? 'success' : 'grey'" label outlined>
            CI {{ project.jobsEnabled ? 'enabled' : 'disabled' }}
          </v-chip>
          <v-chip x-small :color="project.wikiEnabled ? 'success' : 'grey'" label outlined>
            Wiki {{ project.wikiEnabled ? 'enabled' : 'disabled' }}
          </v-chip>
        </div>

        <div class="d-flex justify-space-between mt-3">
          <div class="text-center">
            <v-icon small color="warning">mdi-star</v-icon>
            <div class="caption">{{ project.starCount || 0 }}</div>
          </div>
          <div class="text-center">
            <v-icon small color="primary">mdi-source-fork</v-icon>
            <div class="caption">{{ project.forksCount || 0 }}</div>
          </div>
          <div class="text-center">
            <v-icon small color="error">mdi-alert-circle</v-icon>
            <div class="caption">{{ project.openIssuesCount || 0 }}</div>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
            small
            text
            color="primary"
            :href="project.webUrl"
            target="_blank"
        >
          <v-icon left small>mdi-open-in-new</v-icon>
          Gitlab
        </v-btn>
        <v-btn
            small
            text
            color="grey"
            @click="copyCloneUrl(project)"
        >
          <v-icon left small>mdi-content-copy</v-icon>
          Clone
        </v-btn>
        <v-btn
            small
            text
            color="info"
            @click="showDetails"
        >
          <v-icon left small>mdi-information</v-icon>
          Details
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-hover>
</template>

<script>
export default {
  name: 'ProjectCard',
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  methods: {
    getProjectColor(project) {
      switch (project.visibility) {
        case 'private': return '#FCE4EC';
        case 'internal': return '#E3F2FD';
        case 'public': return '#E8F5E9';
        default: return '#F5F5F5';
      }
    },
    getVisibilityColor(visibility) {
      switch (visibility) {
        case 'private': return '#E91E63';
        case 'internal': return '#2196F3';
        case 'public': return '#4CAF50';
        default: return '#9E9E9E';
      }
    },
    copyCloneUrl(project) {
      navigator.clipboard.writeText(project.httpUrlToRepo)
          .then(() => this.$notify.success('Success', 'Clone URL copied'))
          .catch(() => this.$notify.error('Error', 'Failed to copy URL'));
    },
    showDetails() {
      this.$emit('show-details', this.project);
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.project-card {
  transition: all .3s ease-in-out;
  height: 100%;
}

.project-header {
  background-color: #f5f5f5;
  padding: 12px;
}

.description-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  min-height: 42px;
}

.v-card.on-hover {
  transform: translateY(-4px);
}

.gap-1 {
  gap: 4px;
}

.v-chip {
  margin: 2px;
}
</style>